/**
 * tool-favorite.ts — auth-gated "Add to favorites" (heart) toggle for the public
 * tool page header.
 *
 * The button is only server-rendered for signed-in users (see
 * NewToolPublicPage.cshtml), so this module assumes an authenticated visitor
 * whenever it finds a [data-tn-favorite] trigger.
 *
 * Storage parity: favorites live in AppState using the same key/shape that the
 * legacy tool shell uses, so the public page and shell agree on a single local
 * write-through cache. PersonalizationSync mirrors the toggle to the account in
 * the background (fire-and-forget, non-fatal on failure).
 *
 * On load we hydrate the pressed state from the server (fetchState) so a tool
 * favorited on another device reflects correctly, falling back to the local
 * snapshot when the server is unreachable.
 */
import { query } from "../types/dom.js";
import { contentUrl } from "../localization/locale-runtime.js";
import { trackPersonalizationAction } from "./shared-tracking.js";
import { devLog, devError } from "./dev-utils.js";
import { setIcon } from "./icon-svg.js";
import { AppState } from "./state.js";
import { confirm as confirmModal } from "../components/modal.js";
import { toast } from "../components/toast.js";
function t(key, fallback) {
    const translate = window.tnT;
    return typeof translate === "function" ? translate(key, fallback) : fallback;
}
function personalizationSync() {
    const api = window
        .PersonalizationSync;
    return api ?? null;
}
function trackFavoriteAction(slug, action) {
    trackPersonalizationAction({
        toolSlug: slug,
        interactionType: "favorite",
        action,
        source: "tool-favorite-button",
    });
}
function readFavorites() {
    return AppState.favoriteTools.get();
}
function writeFavorites(entries) {
    AppState.favoriteTools.set(entries);
    return true;
}
function isFavorited(slug) {
    return AppState.favoriteTools.has(slug);
}
function reflect(button, favorited) {
    button.setAttribute("aria-pressed", String(favorited));
    button.title = favorited
        ? t("js.toolfavorite.titleRemove", "Remove from favorites")
        : t("js.toolfavorite.titleAdd", "Add to favorites");
    button.setAttribute("aria-label", favorited
        ? t("js.toolfavorite.ariaRemove", "Remove this tool from my favorites")
        : t("js.toolfavorite.ariaAdd", "Save this tool to my favorites"));
    const icon = button.querySelector(".material-symbols-outlined");
    if (icon !== null) {
        // Trigger animation
        button.classList.add("icon-swap");
        // Change icon mid-animation (at 50% when opacity is 0)
        setTimeout(() => {
            setIcon(icon, favorited ? "favorite" : "favorite_border");
        }, 150);
        // Remove animation class after completion
        setTimeout(() => {
            button.classList.remove("icon-swap");
        }, 300);
    }
    // Remove loading state if present
    button.disabled = false;
    button.classList.remove("is-loading");
}
function setLoading(button, loading) {
    button.disabled = loading;
    if (loading) {
        button.classList.add("is-loading");
    }
    else {
        button.classList.remove("is-loading");
    }
}
// Make the local cache agree with authoritative server state. Without this a
// drifted cache (e.g. a local write that landed while the sync POST was
// failing) survives forever and inverts the *next* toggle, because toggle()
// derives its add/remove direction from the cache, not the server.
function reconcileLocalCache(button, slug, favorited) {
    if (isFavorited(slug) === favorited)
        return;
    const favorites = readFavorites().filter((e) => e.slug !== slug);
    const next = favorited
        ? [
            {
                slug,
                title: button.dataset["toolTitle"] ?? slug,
                category: button.dataset["toolCategory"] ?? "",
                href: button.dataset["toolHref"] ?? `/tools/${slug}`,
                savedAt: new Date().toISOString(),
            },
            ...favorites,
        ]
        : favorites;
    writeFavorites(next);
}
function toggle(button) {
    const slug = button.dataset["toolSlug"];
    if (slug === undefined || slug.trim() === "")
        return;
    // Prevent rapid clicks - check if already processing
    if (button.disabled) {
        devLog("tool-favorite", "Button already processing, ignoring click");
        return;
    }
    // Check auth: if anonymous, show sign-in modal instead of toggling
    const auth = window
        .__TN_AUTH__;
    if (auth?.isAuthenticated !== true) {
        showSignInModal(slug);
        trackFavoriteAction(slug, "prompt_signin");
        return;
    }
    setLoading(button, true);
    const favorites = readFavorites();
    const exists = favorites.some((e) => e.slug === slug);
    const next = exists
        ? favorites.filter((e) => e.slug !== slug)
        : [
            {
                slug,
                title: button.dataset["toolTitle"] ?? slug,
                category: button.dataset["toolCategory"] ?? "",
                href: button.dataset["toolHref"] ?? `/tools/${slug}`,
                savedAt: new Date().toISOString(),
            },
            ...favorites,
        ];
    const writeSuccess = writeFavorites(next);
    if (!writeSuccess) {
        devError("tool-favorite", "Failed to write favorite cache");
        setLoading(button, false);
        toast.show({ message: t("js.toolfavorite.saveFailed", "Failed to save favorite. Please check if browser storage is enabled."), type: "error" });
        return;
    }
    reflect(button, !exists);
    // Track the action
    trackFavoriteAction(slug, exists ? "remove" : "add");
    devLog("tool-favorite", exists ? "Removed" : "Added", "favorite:", slug);
    // Track the timestamp of this local write so hydration doesn't overwrite it.
    button.dataset["lastToggleAt"] = Date.now().toString();
    // Mirror to the account (no-op for anonymous; this button is authed-only).
    try {
        personalizationSync()?.toggleFavorite(slug);
    }
    catch {
        /* sync is best-effort */
    }
}
function showSignInModal(toolSlug) {
    // Store the tool slug so we can auto-favorite after sign-in
    try {
        window.sessionStorage.setItem("tn-pending-favorite", toolSlug);
    }
    catch {
        /* sessionStorage may be disabled */
    }
    void confirmModal(t("js.toolfavorite.signInPrompt", "Sign in to add this tool to your favorites and sync across devices. Redirect to sign in?"), {
        title: t("js.toolfavorite.signInTitle", "Sign in required"),
        riskLevel: "low",
        confirmText: t("js.toolfavorite.signInConfirm", "Sign in"),
        cancelText: t("js.toolfavorite.signInCancel", "Not now"),
    }).then((confirmed) => {
        if (confirmed) {
            // Only the bare `/` honours the locale cookie, so a bare /auth/login is
            // served in English and the visitor loses their language mid-flow. Localize
            // the path and append the already-encoded query rather than handing the whole
            // string to contentUrl, which would round-trip it through URL parsing.
            const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.href = `${contentUrl("/auth/login")}?returnUrl=${returnUrl}`;
        }
    });
}
function hydrate(button) {
    const slug = button.dataset["toolSlug"];
    if (slug === undefined || slug.trim() === "")
        return;
    // Local snapshot first so the icon is correct before the network resolves.
    reflect(button, isFavorited(slug));
    const sync = personalizationSync();
    if (sync === null)
        return;
    setLoading(button, true);
    const hydrateStartedAt = Date.now();
    void sync
        .fetchState(slug)
        .then((state) => {
        setLoading(button, false);
        if (state === null)
            return;
        // Only apply server state if the user hasn't clicked since hydration started.
        // This prevents a race where: user clicks → adds to localStorage → server
        // fetch returns old state → overwrites new state.
        const lastToggleAt = button.dataset["lastToggleAt"];
        if (lastToggleAt !== undefined && parseInt(lastToggleAt, 10) > hydrateStartedAt) {
            // User clicked after hydration started; their local write is newer.
            return;
        }
        reconcileLocalCache(button, slug, state.isFavorited);
        reflect(button, state.isFavorited);
    })
        .catch(() => {
        setLoading(button, false);
        /* fall back to the local snapshot already reflected above */
    });
}
function init() {
    const button = query("[data-tn-favorite]");
    if (button === null)
        return;
    if (button.dataset["tnFavoriteBound"] === "1")
        return;
    button.dataset["tnFavoriteBound"] = "1";
    hydrate(button);
    button.addEventListener("click", () => {
        toggle(button);
    });
    // Keyboard navigation: Enter or Space toggles favorite (WCAG 2.1.1)
    button.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle(button);
        }
    });
    // Auto-favorite after sign-in redirect
    const auth = window
        .__TN_AUTH__;
    if (auth?.isAuthenticated === true) {
        try {
            const pendingSlug = window.sessionStorage.getItem("tn-pending-favorite");
            if (pendingSlug !== null) {
                window.sessionStorage.removeItem("tn-pending-favorite");
                // Only auto-favorite if we're on the same tool page
                const currentSlug = button.dataset["toolSlug"];
                if (pendingSlug === currentSlug) {
                    toggle(button);
                }
            }
        }
        catch {
            /* sessionStorage may be disabled */
        }
    }
    // Multi-tab synchronization: listen for favorite cache changes from other tabs
    window.addEventListener("storage", (event) => {
        if (AppState.favoriteTools.matchesStorageEvent(event) && event.newValue !== null) {
            const slug = button.dataset["toolSlug"];
            if (slug) {
                const nowFavorited = isFavorited(slug);
                reflect(button, nowFavorited);
                devLog("tool-favorite", "Synced state from another tab:", nowFavorited);
            }
        }
    });
}
if (typeof window !== "undefined") {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init, { once: true });
    }
    else {
        init();
    }
}
//# sourceMappingURL=tool-favorite.js.map